'use client'

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createClient } from '@/utils/supabase/client';
import { Tables } from '@/utils/schema';
import { AvatarUploadTrigger } from '@/components/AvatarUploadTrigger';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThemeModeSelect } from '@/components/theme/ThemeModeSelect';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import ColorThief from 'colorthief';
import { toast } from 'sonner';
import { SlideOutPanel } from '@/components/ui/slide-out-panel';
import { HugeiconsIcon } from '@hugeicons/react';
import { EyeIcon, ViewOffSlashIcon, Loading03Icon, Logout01Icon, ArrowLeft01Icon, Edit02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { cn } from '@/lib/utils';

interface AccountContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAvatarUpdated?: () => void;
  className?: string;
}

export function AccountContent({ open, onOpenChange, onAvatarUpdated, className }: AccountContentProps) {
  const [user, setUser] = useState<any>(null);
  const [publicProfile, setPublicProfile] = useState<Tables<'public_profiles'> | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [avatarBase64, setAvatarBase64] = useState<string | null>(null);
  // True after the user clicks the trash button on an existing (persisted)
  // avatar. Distinct from `croppedFile === null`, which is also the initial
  // state. Reset on panel open, on a new file pick, and after a successful save.
  const [avatarCleared, setAvatarCleared] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // 'profile' = read-only view (avatar, name, email, theme, sign out + Edit
  // button). 'edit' = the existing form (display name, password). Slides
  // between the two views inside the same panel.
  const [view, setView] = useState<'profile' | 'edit'>('profile');
  const supabase = createClient();
  const router = useRouter();

  // Whenever the panel re-opens, reset to the read-only profile view so the
  // user lands on a calm overview rather than the form they last left open.
  useEffect(() => {
    if (open) setView('profile');
  }, [open]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        setEmail(user?.email || '');

        if (!user) return;

        // Check if user has password set
        const userHasPassword = user.user_metadata?.has_password_set === true;
        setHasPassword(userHasPassword);

        // Fetch public profile
        let profile: Tables<'public_profiles'> | null = null;
        if (user.user_metadata?.public_profile_id) {
          const { data } = await supabase
            .from('public_profiles')
            .select('*')
            .eq('id', user.user_metadata.public_profile_id)
            .single();
          profile = data as Tables<'public_profiles'> | null;
        } else {
          const { data } = await supabase
            .from('public_profiles')
            .select('*')
            .eq('auth_user_id', user.id)
            .single();
          profile = data as Tables<'public_profiles'> | null;
        }
        
        setPublicProfile(profile);
        setDisplayName(profile?.display_name || '');
        
        if (profile?.avatar_path) {
          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(profile.avatar_path);
          setAvatarUrl(publicUrl);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchUserData();
      // Reset password fields when panel opens
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setAvatarCleared(false);
    }
  }, [open, supabase]);

  const handleCropComplete = async (file: File) => {
    setCroppedFile(file);
    setAvatarCleared(false);
    // Convert to base64 for preview
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      setAvatarBase64(base64);
    };
  };

  const clearAvatar = () => {
    setCroppedFile(null);
    setAvatarBase64(null);
    setAvatarUrl(null);
    setAvatarCleared(true);
  };

  const handleSaveChanges = useCallback(async () => {
    try {
      setIsSaving(true);
      
      // Handle password update if password fields are filled
      const isUpdatingPassword = hasPassword 
        ? (currentPassword || newPassword || confirmPassword)
        : (newPassword || confirmPassword);
      
      if (isUpdatingPassword) {
        // Validate password fields
        if (hasPassword) {
          // User has password - need current password to change it
          if (!currentPassword) {
            toast.error('Please enter your current password');
            setIsSaving(false);
            return;
          }
          
          // Verify current password by attempting to sign in
          const { error: verifyError } = await supabase.auth.signInWithPassword({
            email: email,
            password: currentPassword,
          });
          
          if (verifyError) {
            toast.error('Current password is incorrect');
            setIsSaving(false);
            return;
          }
        }
        
        // Validate new password
        if (!newPassword) {
          toast.error(hasPassword ? 'Please enter a new password' : 'Please enter a password');
          setIsSaving(false);
          return;
        }
        
        if (newPassword.length < 6) {
          toast.error('Password must be at least 6 characters');
          setIsSaving(false);
          return;
        }
        
        if (newPassword !== confirmPassword) {
          toast.error('Passwords do not match');
          setIsSaving(false);
          return;
        }
        
        // Update password
        const hadPasswordBefore = hasPassword;
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword,
          data: {
            has_password_set: true
          }
        });
        
        if (passwordError) {
          toast.error(passwordError.message || 'Error updating password');
          setIsSaving(false);
          return;
        }
        
        // Clear password fields after successful update
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setHasPassword(true);
        toast.success(hadPasswordBefore ? 'Password updated successfully!' : 'Password created successfully!');
      }
      
      // Handle avatar clear (trash button on an existing avatar)
      if (avatarCleared && publicProfile?.avatar_path) {
        // Best-effort storage cleanup. The RLS policy on the `avatars` bucket
        // allows users to delete objects under their own user.id folder, so
        // this runs as the user. If it fails (e.g. file already gone), we
        // still want to clear avatar_path on the profile row below.
        try {
          await supabase.storage.from('avatars').remove([publicProfile.avatar_path]);
        } catch (e) {
          console.warn('Avatar storage cleanup failed (continuing):', e);
        }

        const updateData: any = { avatar_path: null };
        if (displayName !== publicProfile?.display_name) {
          updateData.display_name = displayName;
        }

        // Match the pattern from PR #544: filter by primary key so the
        // UPDATE works regardless of whether the row was loaded by
        // `id = user_metadata.public_profile_id` or by `auth_user_id`.
        if (!publicProfile?.id) {
          throw new Error('No profile loaded; cannot clear avatar.');
        }
        const { error: updateError } = await supabase
          .from('public_profiles')
          .update(updateData)
          .eq('id', publicProfile.id);

        if (updateError) {
          throw new Error(`Failed to clear avatar: ${updateError.message}`);
        }

        // Propagate to auth metadata so the avatar disappears everywhere it's
        // read from `user_metadata.avatar_url`.
        try {
          await supabase.auth.updateUser({ data: { avatar_url: null } });
        } catch {}

        setAvatarUrl(null);
        setAvatarCleared(false);
      }
      // Handle avatar upload if there's a cropped file
      else if (croppedFile) {
        // Extract primary color (non-blocking if it fails)
        let primaryColor: string | null = null;
        try {
          const img = document.createElement('img');
          img.src = URL.createObjectURL(croppedFile);
          img.crossOrigin = 'Anonymous';
          const colorThief = new ColorThief();
          primaryColor = await new Promise<string>((resolve) => {
            if (img.complete) {
              const [r, g, b] = colorThief.getColor(img);
              resolve(`rgb(${r},${g},${b})`);
            } else {
              img.onload = () => {
                const [r, g, b] = colorThief.getColor(img);
                resolve(`rgb(${r},${g},${b})`);
              };
            }
          });
        } catch (_) {
          primaryColor = null;
        }

        const fileExt = 'jpg';
        const timestamp = Date.now();
        const fileName = `${user.id}-${timestamp}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        // Upload to avatar bucket
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, croppedFile, {
            cacheControl: '3600',
            upsert: true,
            contentType: croppedFile.type || 'image/jpeg'
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`Failed to upload avatar: ${uploadError.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        // Update profile with new avatar, optional primary color, and display name
        const updateData: any = { 
          avatar_path: filePath
        };
        if (primaryColor) {
          updateData.primary_color = primaryColor;
        }
        
        // Add display name if it has changed
        if (displayName !== publicProfile?.display_name) {
          updateData.display_name = displayName;
        }

        // Update by primary key. Fetch above is by `id` when
        // user_metadata.public_profile_id is set, otherwise by auth_user_id;
        // updating by `id` works for both paths and avoids a silent 0-row
        // update if the row's auth_user_id ever drifts from the current user.
        if (!publicProfile?.id) {
          throw new Error('No profile loaded; cannot update.');
        }
        const { error: updateError } = await supabase
          .from('public_profiles')
          .update(updateData)
          .eq('id', publicProfile.id);

        if (updateError) {
          console.error('Profile update error:', updateError);
          throw new Error(`Failed to update profile: ${updateError.message}`);
        }

        // Update auth metadata so avatar propagates everywhere
        try {
          await supabase.auth.updateUser({ data: { avatar_url: publicUrl } });
        } catch {}

        // Update the local state
        setAvatarUrl(publicUrl);
        setCroppedFile(null);
        setAvatarBase64(null);
      } else {
        // Only update display name if it has changed
        if (displayName !== publicProfile?.display_name) {
          if (!publicProfile?.id) {
            throw new Error('No profile loaded; cannot update.');
          }
          const { error } = await supabase
            .from('public_profiles')
            .update({ display_name: displayName })
            .eq('id', publicProfile.id);

          if (error) throw error;
        }
      }
      
      // Trigger avatar refresh callback
      onAvatarUpdated?.();
      
      // Close the panel and show success message
      // (Password update success message is already shown above)
      if (!isUpdatingPassword) {
        toast.success('Profile updated successfully!');
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [croppedFile, avatarCleared, publicProfile?.id, publicProfile?.avatar_path, publicProfile?.display_name, displayName, user, supabase, onAvatarUpdated, onOpenChange, hasPassword, currentPassword, newPassword, confirmPassword, email]);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/';
      onOpenChange(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [supabase, onOpenChange]);

  const initial = (publicProfile?.display_name || email || '').trim()[0]?.toUpperCase() ?? '?';

  // Read-only "home" view of the account sheet. Avatar + name + email at the
  // top, theme toggle in the middle, sign out at the bottom. Tapping "Edit
  // profile" slides the panel to the editForm view.
  const profileContent = isLoading ? (
    <div className="flex flex-col justify-center items-center p-6 space-y-4">
      <div className="w-8 h-8 rounded-full border-2 border-atrium-border animate-spin border-t-atrium-black/60"></div>
      <p className="text-sm text-atrium-black/60">Loading account...</p>
    </div>
  ) : !user ? (
    <div className="flex min-h-full flex-col gap-4 bg-atrium-content p-4">
      <p className="text-center text-sm text-atrium-black/70">
        Create a free account to save your work, or sign in to continue.
      </p>
      <Button asChild variant="atrium" className="w-full">
        <Link href="/signup" prefetch={false}>
          Sign up for free
        </Link>
      </Button>
      <Button asChild variant="outline" className="w-full">
        <Link href="/login" prefetch={false}>
          Log in
        </Link>
      </Button>
      <div className="overflow-hidden rounded-xl border border-atrium-border bg-atrium-inner">
        <ThemeModeSelect className="border-0" sectionLabel="Appearance" />
      </div>
    </div>
  ) : (
    <div className="flex flex-col gap-4 p-3 bg-atrium-content min-h-full">
      <div className="flex flex-col items-center gap-3 rounded-xl bg-atrium-inner p-5 border border-atrium-border">
        <Avatar className="size-20">
          <AvatarImage src={avatarUrl || undefined} className="object-cover" />
          <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-linear-to-br from-accent-400 via-accent-600 to-accent-800 text-2xl font-medium text-white">
            {initial}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col items-center gap-0.5 min-w-0 max-w-full">
          <p className="text-base font-semibold text-atrium-black truncate max-w-full">
            {publicProfile?.display_name || 'Your name'}
          </p>
          <p className="text-xs text-atrium-black/55 truncate max-w-full">{email || '—'}</p>
        </div>
        <Button
          type="button"
          onClick={() => setView('edit')}
          variant="outline"
          size="sm"
          className="mt-1 gap-1.5"
        >
          <HugeiconsIcon icon={Edit02Icon} className="size-4" />
          Edit profile
        </Button>
      </div>

      <div className="rounded-xl bg-atrium-inner border border-atrium-border overflow-hidden">
        <ThemeModeSelect className="border-0" sectionLabel="Appearance" />
      </div>

      <p className="px-2 text-center text-xs text-atrium-black/50">
        <Link
          href="/signup"
          prefetch={false}
          className="font-medium text-accent-600 hover:text-accent-700 hover:underline"
        >
          Sign up with another email
        </Link>
      </p>

      <Button
        onClick={handleSignOut}
        variant="ghost"
        size="sm"
        className="gap-2 justify-start text-atrium-black hover:bg-atrium-content"
      >
        <HugeiconsIcon icon={Logout01Icon} className="w-4 h-4" />
        Sign out
      </Button>
    </div>
  );

  // Existing edit form content. Sign-out moved to the profile view; this
  // view focuses on what's actually editable.
  const editContent = isLoading ? (
    <div className="flex flex-col justify-center items-center p-6 space-y-4">
      <div className="w-8 h-8 rounded-full border-2 border-atrium-border animate-spin border-t-atrium-black/60"></div>
      <p className="text-sm text-atrium-black/60">Loading account...</p>
    </div>
  ) : (
    <div className="flex flex-col p-2 space-y-6 bg-atrium-content min-h-full">
      <div className="space-y-6 bg-atrium-inner rounded-lg p-4 border border-atrium-border w-full">
        {/* Avatar Section */}
        <div className="space-y-2">
          <AvatarUploadTrigger
            avatarFile={croppedFile}
            avatarBase64={avatarBase64}
            avatarUrl={avatarUrl}
            inputId="account-avatar-input"
            label="Profile avatar"
            disabled={isSaving}
            submitting={isSaving}
            onCropComplete={handleCropComplete}
            onClear={clearAvatar}
          />
        </div>

        {/* Display Name Section */}
        <div className="space-y-2">
          <Label htmlFor="display-name" variant="atrium">Display Name</Label>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
            className="w-full"
            variant="white"
          />
        </div>

        {/* Email Section */}
        <div className="space-y-2">
          <Label htmlFor="email" variant="atrium">Email Address</Label>
          <Input
            id="email"
            value={email}
            disabled
            variant="white"
            className="w-full"
          />
        </div>

        {/* Password Section */}
        <div className="space-y-4 pt-4 border-t border-atrium-border">
          <div className="space-y-1">
            <Label variant="atrium">
              {hasPassword ? 'Change Password' : 'Create Password'}
            </Label>
            <p className="text-xs text-atrium-black/60">
              {hasPassword 
                ? 'Enter your current password and a new password to change it.'
                : 'Create a password to enable password-based login for your account.'}
            </p>
          </div>

          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="current-password" variant="atrium">Current Password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="w-full pr-10"
                  variant="white"
                  disabled={isSaving}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-atrium-black/60 hover:text-atrium-black"
                  disabled={isSaving}
                >
                  {showCurrentPassword ? (
                    <HugeiconsIcon icon={ViewOffSlashIcon} className="w-4 h-4" />
                  ) : (
                    <HugeiconsIcon icon={EyeIcon} className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-password" variant="atrium">
              {hasPassword ? 'New Password' : 'Password'}
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={hasPassword ? 'Enter new password' : 'Enter password'}
                className="w-full pr-10"
                variant="white"
                disabled={isSaving}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-atrium-black/60 hover:text-atrium-black"
                disabled={isSaving}
              >
                {showNewPassword ? (
                  <HugeiconsIcon icon={ViewOffSlashIcon} className="w-4 h-4" />
                ) : (
                  <HugeiconsIcon icon={EyeIcon} className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" variant="atrium">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="w-full pr-10"
                variant="white"
                disabled={isSaving}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-atrium-black/60 hover:text-atrium-black"
                disabled={isSaving}
              >
                {showConfirmPassword ? (
                  <HugeiconsIcon icon={ViewOffSlashIcon} className="w-4 h-4" />
                ) : (
                  <HugeiconsIcon icon={EyeIcon} className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Title + content + footer all swap based on `view`. The panel slides the
  // content area horizontally so the transition feels like drilling into a
  // sub-sheet (cf. iOS settings).
  const isEdit = view === 'edit';
  const title = isEdit ? 'Edit profile' : 'Profile';
  const footer = isEdit ? (
    <div className="flex justify-start gap-2">
      <Button onClick={() => setView('profile')} variant="outline">
        Cancel
      </Button>
      <Button
        onClick={async () => {
          await handleSaveChanges();
        }}
        variant="atrium"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <HugeiconsIcon icon={Loading03Icon} className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>Save</>
        )}
      </Button>
    </div>
  ) : null;

  return (
    <SlideOutPanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      direction="right"
      className={className || 'w-full lg:max-w-md'}
      footer={footer}
    >
      {/* Two-pane horizontal slide. Both views are mounted so form state in
          the edit view survives drilling back to profile and forward again. */}
      <div className="relative h-full overflow-hidden">
        <div
          className={cn(
            'flex h-full w-[200%] transition-transform duration-300 ease-out motion-reduce:transition-none',
            isEdit ? '-translate-x-1/2' : 'translate-x-0',
          )}
        >
          <div className="w-1/2 shrink-0 overflow-y-auto" aria-hidden={isEdit}>
            {profileContent}
          </div>
          <div className="w-1/2 shrink-0 overflow-y-auto" aria-hidden={!isEdit}>
            {/* Drill-back chevron at the top of the edit view. The footer Cancel
                does the same thing; this is an iOS-style affordance. */}
            <div className="flex items-center px-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setView('profile')}
                aria-label="Back to profile"
                className="-ml-1 gap-1 text-atrium-black/60 hover:text-atrium-black"
              >
                <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
                Back
              </Button>
            </div>
            {editContent}
          </div>
        </div>
      </div>
    </SlideOutPanel>
  );
}
